import React, { useState, useEffect } from 'react';
import './Pricing.css';
import { apiRequest } from '../services/apiInterceptor';

function Pricing() {
  // State for packages and plans
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch packages when component mounts
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        const packagesResponse = await apiRequest('/api/package');
        
        if (packagesResponse && Array.isArray(packagesResponse)) {
          setPackages(packagesResponse);
          // Auto-select the first package to show plans immediately
          if (packagesResponse.length > 0) {
            const firstPackage = packagesResponse[0];
            setSelectedPackage(firstPackage);
            await fetchPlansForPackage(firstPackage);
          }
        } else {
          setPackages([]);
          setError('No packages available at the moment.');
        }
      } catch (err) {
        console.error('Error fetching packages:', err);
        setError('Failed to load packages. Please try again later.');
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // Fetch plans and filter by selected package
  const fetchPlansForPackage = async (packageObj) => {
    try {
      setPlansLoading(true);
      
      const allPlans = await apiRequest('/api/plan');
      setPlans(allPlans || []);
      
      // Fetch rates and info for each plan and filter by selected package
      const plansWithDetails = await Promise.all(
        (allPlans || []).map(async (plan) => {
          const planId = plan.id || plan.Id;
          
          try {
            const [rates, info] = await Promise.all([
              apiRequest(`/api/plan/${planId}/rate`).catch(() => []),
              apiRequest(`/api/plan/${planId}/info`).catch(() => [])
            ]);

            // Filter rates for the selected package
            const packageRates = rates.filter(rate => 
              (rate.packageId || rate.PackageId) === (packageObj.id || packageObj.Id)
            );

            return {
              ...plan,
              rates: packageRates,
              info: info || []
            };
          } catch (error) {
            console.warn(`Error fetching details for plan ${planId}:`, error);
            return {
              ...plan,
              rates: [],
              info: []
            };
          }
        })
      );
      
      // Only show plans that have rates for this package
      const filteredPlansWithDetails = plansWithDetails.filter(plan => 
        plan.rates && plan.rates.length > 0
      );
      
      setFilteredPlans(filteredPlansWithDetails);
      
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans. Please try again.');
    } finally {
      setPlansLoading(false);
    }
  };

  // Handle package selection
  const handlePackageSelection = async (packageObj) => {
    setSelectedPackage(packageObj);
    await fetchPlansForPackage(packageObj);
  };

  // Handle billing period change
  const handleBillingPeriodChange = (period) => {
    setBillingPeriod(period);
  };

  // Handle "Get Started" button click
  const handleGetStarted = () => {
    window.location.href = 'https://app.examflip.com/create-account';
  };

  // Render loading state
  if (loading) {
    return (
      <div className="pricing-page-wrapper dark-mode">
        <div className="pricing-container">
          <div className="loading-message">Loading pricing information...</div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="pricing-page-wrapper dark-mode">
        <div className="pricing-container">
          <div className="error-message">{error}</div>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-page-wrapper dark-mode">
      <a
        href="https://www.examflip.com"
        className="examflip-logo-link"
        style={{
          position: 'absolute',
          top: '24px',
          left: '32px',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          textDecoration: 'none',
        }}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          src={require('../assets/dark_green.png')}
          alt="ExamFlip Logo"
          style={{ height: '40px', width: '40px', marginRight: '10px', objectFit: 'contain' }}
        />
        <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.3rem', letterSpacing: '1px' }}>ExamFlip</span>
      </a>
      <div className="pricing-container">
        {/* Header */}
        <div className="pricing-header">
          <h1>Pricing</h1>
          {/* <p className="pricing-subtitle">
            Select the plan to see pricing
          </p> */}
        </div>

        {/* Package Selection */}
        {packages.length > 1 && (
          <div className="packages-section">
            <h2>Select a Package</h2>
            <div className="packages-grid">
              {packages.map(pkg => (
                <div 
                  key={pkg.id || pkg.Id}
                  className={`package-card ${selectedPackage && (selectedPackage.id || selectedPackage.Id) === (pkg.id || pkg.Id) ? 'selected' : ''}`}
                  onClick={() => handlePackageSelection(pkg)}
                >
                  <h3>{pkg.name || pkg.Name || 'Package'}</h3>
                  <p className="package-description">
                    {pkg.description || pkg.Description || 'No description available'}
                  </p>
                  <button className="select-package-button">
                    {selectedPackage && (selectedPackage.id || selectedPackage.Id) === (pkg.id || pkg.Id) ? 'Selected' : 'Select Package'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Divider between packages and billing toggle */}
        <div className="package-divider"></div>

        {/* Billing Period Toggle */}
        <div className="billing-toggle-container">
          <div className="billing-toggle">
            <button
              className={billingPeriod === 'monthly' ? 'active' : ''}
              onClick={() => handleBillingPeriodChange('monthly')}
            >
              Monthly
            </button>
            <button
              className={billingPeriod === 'annual' ? 'active' : ''}
              onClick={() => handleBillingPeriodChange('annual')}
            >
              Annual (Save up to 25%)
            </button>
          </div>
        </div>

        {/* Plans Section */}
        <div className="plans-section">
          {selectedPackage && (
            <h2>Plans for {selectedPackage.name || selectedPackage.Name}</h2>
          )}

          {plansLoading ? (
            <div className="loading-message">Loading plans...</div>
          ) : (
            <div className="plans-grid">
              {[...filteredPlans]
                .sort((a, b) => {
                  // Sort plans by price (ascending)
                  const aRate = a.rates.find(r =>
                    billingPeriod === 'monthly'
                      ? r.monthlyPrice !== null && r.monthlyPrice !== undefined
                      : r.annualPrice !== null && r.annualPrice !== undefined
                  );
                  const bRate = b.rates.find(r =>
                    billingPeriod === 'monthly'
                      ? r.monthlyPrice !== null && r.monthlyPrice !== undefined
                      : r.annualPrice !== null && r.annualPrice !== undefined
                  );
                  const aPrice = billingPeriod === 'monthly' ? aRate?.monthlyPrice ?? Infinity : aRate?.annualPrice ?? Infinity;
                  const bPrice = billingPeriod === 'monthly' ? bRate?.monthlyPrice ?? Infinity : bRate?.annualPrice ?? Infinity;
                  return aPrice - bPrice;
                })
                .map(plan => {
                  const rate = plan.rates.find(r => {
                    if (billingPeriod === 'monthly') {
                      return r.monthlyPrice !== null && r.monthlyPrice !== undefined;
                    } else {
                      return r.annualPrice !== null && r.annualPrice !== undefined;
                    }
                  });

                  const price = billingPeriod === 'monthly' 
                    ? rate?.monthlyPrice 
                    : rate?.annualPrice;

                  const planName = plan.name || plan.Name || 'Plan';
                  const isPopular = planName.toLowerCase().includes('premium');
                  const isFree = price === 0 || planName.toLowerCase() === 'free';

                  return (
                    <div 
                      key={plan.id || plan.Id}
                      className={`plan-card ${isPopular ? 'featured' : ''}`}
                    >
                      {isPopular && <span className="best-value-tag">BEST VALUE</span>}
                      
                      <div className="plan-header">
                        <h3>{planName}</h3>
                        <div className="plan-price">
                          <span className="price-amount">
                            {isFree ? 'Free' : `$${price !== null && price !== undefined ? price.toFixed(2) : 'N/A'}`}
                          </span>
                          {!isFree && (
                            <span className="price-period">
                              /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="plan-description">
                        {plan.description || plan.Description || 'Comprehensive exam preparation'}
                      </div>

                      {/* Render plan features */}
                      <div className="plan-features">
                        {plan.info && plan.info.length > 0 ? (
                          plan.info.map((feature, index) => {
                            const isIncluded = feature.isIncluded !== false && feature.IsIncluded !== false;
                            return (
                              <div key={index} className={`feature ${isIncluded ? 'included' : 'excluded'}`}>
                                <span className="feature-check">
                                  {isIncluded ? '✓' : '✗'}
                                </span>
                                <div className="feature-content">
                                  <div className="feature-label">
                                    {feature.feature || feature.Feature || 'Feature'}
                                  </div>
                                  {(feature.detail || feature.Detail) && (
                                    <div className="feature-detail">
                                      {feature.detail || feature.Detail}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          // Fallback features based on plan name if no info available
                          <>
                            <div className="feature included">
                              <span className="feature-check">✓</span>
                              <div className="feature-content">
                                <div className="feature-label">Practice Test</div>
                                <div className="feature-detail">
                                  {planName.toLowerCase() === 'free' ? 'up to 5 questions per day' : 'Unlimited'}
                                </div>
                              </div>
                            </div>
                            
                            {planName.toLowerCase() !== 'free' && (
                              <>
                                <div className="feature included">
                                  <span className="feature-check">✓</span>
                                  <div className="feature-content">
                                    <div className="feature-label">Question Bank</div>
                                    <div className="feature-detail">Unlimited</div>
                                  </div>
                                </div>
                                
                                <div className="feature included">
                                  <span className="feature-check">✓</span>
                                  <div className="feature-content">
                                    <div className="feature-label">Simulate Exam</div>
                                    <div className="feature-detail">
                                      {planName.toLowerCase().includes('standard') ? '5 Exam per month' : 'Unlimited'}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="feature included">
                                  <span className="feature-check">✓</span>
                                  <div className="feature-content">
                                    <div className="feature-label">Performance Analytics</div>
                                    <div className="feature-detail">Unlimited</div>
                                  </div>
                                </div>
                              </>
                            )}

                            {planName.toLowerCase().includes('premium') && (
                              <>
                                <div className="feature included">
                                  <span className="feature-check">✓</span>
                                  <div className="feature-content">
                                    <div className="feature-label">Adaptive Strength & Weakness Analyzer</div>
                                  </div>
                                </div>
                                
                                <div className="feature included">
                                  <span className="feature-check">✓</span>
                                  <div className="feature-content">
                                    <div className="feature-label">OSCE Simulation (coming soon)</div>
                                  </div>
                                </div>
                                
                                <div className="feature included">
                                  <span className="feature-check">✓</span>
                                  <div className="feature-content">
                                    <div className="feature-label">AI Explain (coming soon)</div>
                                  </div>
                                </div>
                                
                                <div className="feature included">
                                  <span className="feature-check">✓</span>
                                  <div className="feature-content">
                                    <div className="feature-label">AI Chat (coming soon)</div>
                                  </div>
                                </div>
                              </>
                            )}

                            {(planName.toLowerCase() === 'free' || planName.toLowerCase().includes('basic')) && (
                              <>
                                <div className="feature excluded">
                                  <span className="feature-check">✗</span>
                                  <div className="feature-content">
                                    <div className="feature-label">Performance Analytics</div>
                                  </div>
                                </div>
                                
                                <div className="feature excluded">
                                  <span className="feature-check">✗</span>
                                  <div className="feature-content">
                                    <div className="feature-label">Adaptive Strength & Weakness Analyzer</div>
                                  </div>
                                </div>
                                
                                <div className="feature excluded">
                                  <span className="feature-check">✗</span>
                                  <div className="feature-content">
                                    <div className="feature-label">OSCE Simulation</div>
                                  </div>
                                </div>
                                
                                <div className="feature excluded">
                                  <span className="feature-check">✗</span>
                                  <div className="feature-content">
                                    <div className="feature-label">AI Explain</div>
                                  </div>
                                </div>
                                
                                <div className="feature excluded">
                                  <span className="feature-check">✗</span>
                                  <div className="feature-content">
                                    <div className="feature-label">AI Chat</div>
                                  </div>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      
                      <button
                        className={`get-started-button ${isFree ? 'free-plan' : ''}`}
                        onClick={() => handleGetStarted(plan, rate)}
                      >
                        {isFree ? 'Start Free' : 'Get Started'}
                      </button>
                    </div>
                  );
                })}
            </div>
          )}

          {filteredPlans.length === 0 && !plansLoading && selectedPackage && (
            <div className="no-plans-message">
              <h3>No plans available for this package</h3>
              <p>This package doesn't have any available plans at the moment. Please try selecting a different package or contact support.</p>
            </div>
          )}
        </div>

        {/* Guarantee Section
        <div className="guarantee-section">
          <div className="guarantee-content">
            <div className="guarantee-icon">🛡️</div>
            <div className="guarantee-text">
              <h3>30-Day Money-Back Guarantee</h3>
              <p>Not satisfied? Get a full refund within 30 days, no questions asked.</p>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
}

export default Pricing;
